import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAddPro017ListItemsMutation,
  useCreatePro017CampaignMutation,
  useGetPro017CampaignByCodeQuery,
  useGetPro017ListItemsQuery,
  useUpdatePro017CampaignMutation,
  useValidatePro017ConflictsMutation,
} from "@/services/campaign";
import { toast } from "@/components/ui/use-toast";
import { CampaignType } from "@/constants/campaign-types";
import { CampaignConflictModalContent } from "../../components/campaign-conflict-modal";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { getTranslatedErrorMessage, isCampaignConflictError } from "../../utils/error-messages";
import {
  mergeConflicts,
  dropStagedCampaigns,
  mergePendingRemoval,
  buildConflictResolutions,
  type CampaignConflictItem,
  type PendingRemoval,
} from "../../utils/conflict-resolution";
import { useConflictResolution } from "../hooks/use-conflict-resolution";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import StepIndicator, { Step } from "./step-indicator";
import { CampaignPreviewHeader } from "./campaign-preview-header";
import StepOne, { Pro017StepOneData } from "./steps-pro017/step-one";
import StepTwo, { Pro017StepTwoData } from "./steps-pro017/step-two";
import StepThree, { Pro017StepThreeData } from "./steps-pro017/step-three";
import StepFour, { Pro017StepFourData } from "./steps-pro017/step-four";
import StepFive, { Pro017CampaignData } from "./steps-pro017/step-five";
import { CONFLICT_PREVALIDATION_CHUNK_SIZE, LARGE_CAMPAIGN_ITEM_THRESHOLD } from "../constants";

interface CreatePro017FormProps {
  onChangeType?: (type: CampaignType) => void;
  mode?: 'create' | 'edit';
  campaignCode?: number;
}

export interface Pro017List {
  listNumber: number;
  items: string[];
}

const pro017Steps: Step[] = [
  { number: 1, title: "Tipo" },
  { number: 2, title: "Desconto" },
  { number: 3, title: "Listas" },
  { number: 4, title: "Produtos" },
  { number: 5, title: "Revisão" },
];

export default function CreatePro017Form({ onChangeType, mode = 'create', campaignCode }: CreatePro017FormProps) {
  const navigate = useNavigate();
  const [createCampaign, { isLoading: isCreating }] = useCreatePro017CampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] = useUpdatePro017CampaignMutation();
  const [addListItems] = useAddPro017ListItemsMutation();
  const [validateConflicts] = useValidatePro017ConflictsMutation();
  const { handleModal } = useDialogModal();
  const [originalList1Skus, setOriginalList1Skus] = useState<Set<string>>(new Set());
  const [originalList2Skus, setOriginalList2Skus] = useState<Set<string>>(new Set());
  const [isOriginalHydrated, setIsOriginalHydrated] = useState(false);

  // Fetch campaign data in edit mode
  const { data: existingCampaign, isLoading: isLoadingCampaign } = useGetPro017CampaignByCodeQuery(
    campaignCode!,
    { skip: mode !== 'edit' || !campaignCode }
  );

  // Fetch list 1 items
  const { data: list1Items, isLoading: isLoadingList1 } = useGetPro017ListItemsQuery(
    { code: campaignCode!, listNumber: 1 },
    { skip: mode !== 'edit' || !campaignCode }
  );

  // Fetch list 2 items
  const { data: list2Items, isLoading: isLoadingList2 } = useGetPro017ListItemsQuery(
    { code: campaignCode!, listNumber: 2 },
    { skip: mode !== 'edit' || !campaignCode }
  );

  const isLoading = isCreating || isUpdating;
  const [currentStep, setCurrentStep] = useState(1);
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<PendingRemoval[]>([]);
  const stageRemoval = (removal: PendingRemoval) =>
    setPendingRemovals((prev) => mergePendingRemoval(prev, removal));
  const unstageRemoval = (itemCode: string) =>
    setPendingRemovals((prev) => prev.filter((p) => p.itemCode !== itemCode));
  const [conflictFilter, setConflictFilter] = useState<"all" | "conflict" | "clean">("all");
  const [validationProgress, setValidationProgress] = useState<{ current: number; total: number } | null>(null);
  const [campaignData, setCampaignData] = useState<Pro017CampaignData>({
    description: "",
    startDate: "",
    endDate: "",
    discountValue: 0,
    discountType: 1, // Percentual como padrão
    lists: [
      { listNumber: 1, items: [] },
      { listNumber: 2, items: [] },
    ],
  });

  const totalSteps = 5;

  // Pre-populate data when in edit mode
  useEffect(() => {
    if (mode === 'edit' && existingCampaign?.data && list1Items?.items && list2Items?.items) {
      const campaign = existingCampaign.data;
      // Normaliza (trim+uppercase) pra que a comparação vs SKUs adicionados pelo
      // usuário (também normalizados em StepFour.handleAddItem) seja confiável.
      // Evita que itens já salvos apareçam como "NOVO" por conta de padding CHAR
      // retornado pelo Oracle ou case diferente.
      const normalize = (s: string) => String(s).trim().toUpperCase();
      const list1Codes = list1Items.items.map((item: any) => normalize(item.code));
      const list2Codes = list2Items.items.map((item: any) => normalize(item.code));

      setCampaignData({
        description: campaign.description || "",
        startDate: campaign.startDate || "",
        endDate: campaign.endDate || "",
        discountValue: campaign.discountValue || 0,
        discountType: campaign.discountType || 1,
        lists: [
          { listNumber: 1, items: list1Codes },
          { listNumber: 2, items: list2Codes },
        ],
      });

      setOriginalList1Skus(new Set(list1Codes));
      setOriginalList2Skus(new Set(list2Codes));
      setIsOriginalHydrated(true);
    }
  }, [mode, existingCampaign, list1Items, list2Items]);

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateData = (data: Partial<Pro017CampaignData>) => {
    setCampaignData({ ...campaignData, ...data });
    if (data.lists || data.startDate || data.endDate) {
      if (conflicts.length > 0) setConflicts([]);
      if (pendingRemovals.length > 0) setPendingRemovals([]);
    }
  };

  const prevalidateConflicts = async (): Promise<CampaignConflictItem[]> => {
    const flattened = campaignData.lists.flatMap((list) =>
      list.items.map((item) => ({ listNumber: list.listNumber, item })),
    );
    const totalChunks = Math.max(1, Math.ceil(flattened.length / CONFLICT_PREVALIDATION_CHUNK_SIZE));
    const found: CampaignConflictItem[] = [];

    if (flattened.length >= LARGE_CAMPAIGN_ITEM_THRESHOLD) {
      setValidationProgress({ current: 0, total: totalChunks });
    }

    for (let index = 0; index < flattened.length; index += CONFLICT_PREVALIDATION_CHUNK_SIZE) {
      const chunk = flattened.slice(index, index + CONFLICT_PREVALIDATION_CHUNK_SIZE);
      const lists = Array.from(
        chunk.reduce((map, entry) => {
          map.set(entry.listNumber, [...(map.get(entry.listNumber) || []), entry.item]);
          return map;
        }, new Map<number, string[]>()).entries(),
      ).map(([listNumber, items]) => ({ listNumber, items }));

      setValidationProgress({ current: Math.floor(index / CONFLICT_PREVALIDATION_CHUNK_SIZE) + 1, total: totalChunks });
      const result = await validateConflicts({
        startDate: campaignData.startDate,
        endDate: campaignData.endDate,
        lists,
      }).unwrap();
      found.push(...result.conflicts);
    }

    setValidationProgress(null);
    return mergeConflicts(found);
  };

  // Resolução de conflito direto na lista de produtos (StepFour). Mantém o SKU
  // nas listas e apenas ESTAGIA a remoção das campanhas anteriores.
  const discardSkuFromLists = (itemCode: string) =>
    setCampaignData((prev) => ({
      ...prev,
      lists: prev.lists.map((list) => ({
        ...list,
        items: list.items.filter((sku) => sku !== itemCode),
      })),
    }));

  const { handleRemoveFromPrevious: removeConflictFromList } =
    useConflictResolution({
      conflicts,
      setConflicts,
      discardItemLocally: discardSkuFromLists,
      stageRemoval,
      onAllResolved: () => {},
    });

  // Abre o modal de resolução de conflito no <Modal/> global (singleton).
  const openConflictModal = (
    items: CampaignConflictItem[],
    unresolvableItemCodes?: string[],
  ) => {
    handleModal({
      isOpen: true,
      title: CampaignConflictModalContent.title,
      element: (
        <CampaignConflictModalContent
          initialConflicts={items}
          discardItemLocally={discardSkuFromLists}
          onStageRemoval={stageRemoval}
          onUnstageRemoval={unstageRemoval}
          onConflictsChange={setConflicts}
          onGoBack={() => setCurrentStep(4)}
          unresolvableItemCodes={unresolvableItemCodes}
        />
      ),
    });
  };

  // Backend não conseguiu remover certos itens das anteriores (ex.: mín. 2):
  // reabre o modal forçando o descarte deles (Remover fica desabilitado).
  const handleUnresolvableConflict = (codes: string[], message?: string) => {
    const codeSet = new Set(codes);
    const failed = pendingRemovals.filter((p) => codeSet.has(p.itemCode));
    setPendingRemovals((prev) => prev.filter((p) => !codeSet.has(p.itemCode)));
    setConflicts(failed);
    setConflictFilter("conflict");
    openConflictModal(failed, codes);
    toast({
      title: "Não foi possível remover",
      description:
        message ||
        "Alguns produtos não podem ser removidos da campanha anterior. Descarte-os para continuar.",
      variant: "destructive",
    });
  };

  const handleSubmit = async () => {
    if (conflicts.length > 0) {
      openConflictModal(conflicts);
      return;
    }
    await submitCampaign();
  };

  // Pré-valida e cria/edita de fato (sem o guard de conflito). Reusado pelo
  // submit normal e pelo callback de "todos os conflitos resolvidos".
  const submitCampaign = async () => {
    try {
      // Desconsidera os conflitos já marcados para remoção ao criar (staging).
      const prevalidationConflicts = dropStagedCampaigns(
        await prevalidateConflicts(),
        pendingRemovals,
      );
      if (prevalidationConflicts.length > 0) {
        setConflicts(prevalidationConflicts);
        setConflictFilter("conflict");
        openConflictModal(prevalidationConflicts);
        toast({
          title: "Conflito de produtos",
          description: `${prevalidationConflicts.length} produto(s) já estão em outras campanhas ativas no mesmo período.`,
          variant: "destructive",
        });
        return;
      }

      if (mode === 'create') {
        const allSkus = new Set(campaignData.lists.flatMap((list) => list.items));
        const payload = {
          description: campaignData.description,
          startDate: campaignData.startDate,
          endDate: campaignData.endDate,
          discountValue: campaignData.discountValue,
          discountType: campaignData.discountType,
          lists: campaignData.lists,
          conflictResolutions: buildConflictResolutions(pendingRemovals, allSkus),
        };

        console.log('Payload PRO017 (CREATE):', payload);

        const result = await createCampaign(payload).unwrap();
        setPendingRemovals([]);

        if ("queued" in result.data) {
          toast({
            title: "Criação em segundo plano",
            description: `Campanha "${campaignData.description}" enviada para a fila (Job #${result.data.jobId}). Você será notificado ao concluir.`,
            variant: "default",
          });
          setTimeout(() => {
            navigate("/planejamento-comercial/gestao-campanhas");
          }, 1500);
          return;
        }

        toast({
          title: "Sucesso!",
          description: `Campanha "${campaignData.description}" #${result.data.code} criada com sucesso!`,
          variant: "default",
        });

        setTimeout(() => {
          navigate("/planejamento-comercial/gestao-campanhas");
        }, 1500);
      } else {
        // Edit mode — guard against submit before RTK Query data hydrated
        if (!isOriginalHydrated) {
          toast({
            title: "Aguarde",
            description: "Os dados da campanha ainda estão sendo carregados. Tente novamente em instantes.",
            variant: "default",
          });
          return;
        }

        const updatePayload = {
          code: campaignCode!,
          description: campaignData.description,
          startDate: campaignData.startDate,
          endDate: campaignData.endDate,
        };

        console.log('Payload PRO017 (UPDATE):', updatePayload);

        await updateCampaign(updatePayload).unwrap();

        // Persist new list items (diff vs original snapshot) via addListItems per list.
        // Partial-failure handling: track outcome per list; report accurately.
        const list1Current = campaignData.lists.find((l) => l.listNumber === 1)?.items ?? [];
        const list2Current = campaignData.lists.find((l) => l.listNumber === 2)?.items ?? [];
        const list1New = list1Current.filter((sku) => !originalList1Skus.has(sku));
        const list2New = list2Current.filter((sku) => !originalList2Skus.has(sku));

        const listsToAdd = [
          { listNumber: 1 as const, skus: list1New },
          { listNumber: 2 as const, skus: list2New },
        ].filter((l) => l.skus.length > 0);

        const listFailures: Array<{ listNumber: number; count: number; error: unknown }> = [];
        let listsSucceeded = 0;
        let itemsAdded = 0;

        for (const { listNumber, skus } of listsToAdd) {
          try {
            await addListItems({ code: campaignCode!, listNumber, itemCodes: skus }).unwrap();
            listsSucceeded += 1;
            itemsAdded += skus.length;
          } catch (err) {
            listFailures.push({ listNumber, count: skus.length, error: err });
          }
        }

        if (listFailures.length > 0) {
          const failedLists = listFailures.map((f) => `Lista ${f.listNumber}`).join(", ");
          const failedCount = listFailures.reduce((acc, f) => acc + f.count, 0);
          toast({
            title: "Parcialmente concluído",
            description: `Dados gerais atualizados; falha ao adicionar ${failedCount} item(ns) em ${failedLists}. Reabra para tentar novamente.`,
            variant: "destructive",
          });
        } else {
          const extraDesc = itemsAdded > 0 ? ` (${itemsAdded} novo(s) item(ns) adicionado(s))` : "";
          toast({
            title: "Sucesso!",
            description: `Campanha "${campaignData.description}" #${campaignCode} atualizada com sucesso!${extraDesc}`,
            variant: "default",
          });
        }

        setTimeout(() => {
          navigate("/planejamento-comercial/gestao-campanhas");
        }, 1500);
      }
    } catch (error: any) {
      setValidationProgress(null);
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} PRO017 campaign:`, error);

      if (error?.status === 409 && error?.data?.unresolvableItemCodes) {
        handleUnresolvableConflict(error.data.unresolvableItemCodes, error.data.message);
      } else if (isCampaignConflictError(error) && error?.data?.conflicts) {
        setConflicts(error.data.conflicts);
        setConflictFilter("conflict");
        openConflictModal(error.data.conflicts);

        toast({
          title: "Conflito de produtos",
          description: `${error.data.conflicts.length} produto(s) já estão em outras campanhas ativas no mesmo período.`,
          variant: "destructive",
        });
      } else {
        const errorMessage = getTranslatedErrorMessage(
          error,
          `Erro ao ${mode === 'create' ? 'criar' : 'atualizar'} campanha PRO017. Verifique os dados e tente novamente.`
        );

        toast({
          title: "Erro",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleNavigateToStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  };

  if (isLoadingCampaign || isLoadingList1 || isLoadingList2) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando dados da campanha...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {mode === 'edit' && campaignCode && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertDescription>
            Modo Edição - Campanha PRO017 #{campaignCode}
          </AlertDescription>
        </Alert>
      )}

      <StepIndicator
        currentStep={currentStep}
        totalSteps={totalSteps}
        steps={pro017Steps}
        onStepClick={handleNavigateToStep}
        requireConfirmationFrom={2}
      >
        <CampaignPreviewHeader
          campaignType="PRO017"
          description={campaignData.description}
          startDate={campaignData.startDate}
          endDate={campaignData.endDate}
          discountValue={campaignData.discountValue}
          discountType={campaignData.discountType}
          listsCount={campaignData.lists.filter(l => l.items.length > 0).length}
          productsCount={campaignData.lists.reduce((sum, list) => sum + list.items.length, 0)}
        />
      </StepIndicator>

      <div className="mt-4">
        {currentStep === 1 && (
          <StepOne
            data={{
              description: campaignData.description,
              startDate: campaignData.startDate,
              endDate: campaignData.endDate,
            }}
            updateData={(data: Partial<Pro017StepOneData>) => updateData(data)}
            nextStep={nextStep}
            onChangeType={onChangeType}
          />
        )}
        {currentStep === 2 && (
          <StepTwo
            data={{
              discountValue: campaignData.discountValue,
              discountType: campaignData.discountType,
            }}
            updateData={(data: Partial<Pro017StepTwoData>) => updateData(data)}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        )}
        {currentStep === 3 && (
          <StepThree
            data={{
              lists: campaignData.lists,
            }}
            updateData={(data: Partial<Pro017StepThreeData>) => updateData(data)}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        )}
        {currentStep === 4 && (
          <StepFour
            data={{
              lists: campaignData.lists,
            }}
            updateData={(data: Partial<Pro017StepFourData>) => updateData(data)}
            nextStep={nextStep}
            prevStep={prevStep}
            mode={mode}
            originalSkusPerList={{
              1: originalList1Skus,
              2: originalList2Skus,
            }}
            conflicts={conflicts}
            conflictFilter={conflictFilter}
            onConflictFilterChange={setConflictFilter}
            onRemoveFromPrevious={removeConflictFromList}
          />
        )}
        {currentStep === 5 && (
          <StepFive
            data={campaignData}
            prevStep={prevStep}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            mode={mode}
            conflicts={conflicts}
            onEditProducts={() => setCurrentStep(4)}
            originalSkusPerList={{
              1: originalList1Skus,
              2: originalList2Skus,
            }}
            validationProgress={validationProgress}
          />
        )}
      </div>
    </div>
  );
}
